/**
 * Cloudinary delivery root for all Suitsupply imagery.
 *
 * Note this is the *global* CDN — China deployments serve fonts and static
 * assets from their own host (see `chinaCdn.ts` in `@susu/customer-hub`), but
 * product imagery still comes from here.
 */
export const CDN_IMAGE_BASE = 'https://cdn.suitsupply.com/image/upload'

interface CdnImageOptions {
  /** Rendered width in px, mapped to Cloudinary's `w_` transformation. */
  width?: number
  /** Crop to fill the requested box (`c_fill`). Off for free-standing art. */
  fill?: boolean
}

/**
 * Build a CDN url for a Cloudinary image path.
 *
 * Takes the path *relative to the upload root* — e.g.
 * `products/trousers/default/b25206_28` — and prefixes it with the base and
 * the standard `f_auto,q_auto` transformations. Returns `undefined` for a
 * missing path so callers can fall back to a placeholder instead of
 * requesting `.../undefined`.
 */
export function buildCdnImageUrl(
  imagePath: string,
  options?: CdnImageOptions,
): string
export function buildCdnImageUrl(
  imagePath: string | null | undefined,
  options?: CdnImageOptions,
): string | undefined
export function buildCdnImageUrl(
  imagePath: string | null | undefined,
  { width = 360, fill = true }: CdnImageOptions = {},
): string | undefined {
  if (!imagePath) return undefined

  const transformations = [
    `w_${width}`,
    ...(fill ? ['c_fill'] : []),
    'f_auto',
    'q_auto',
  ].join(',')

  return `${CDN_IMAGE_BASE}/${transformations}/${imagePath.replace(/^\/+/, '')}`
}

/**
 * Per-category "hero" view suffix. The producer hands us whatever view it has
 * on file (often `_1`), but each category has one canonical render the cards
 * are designed around — `_25` for a coat on a bust, `_28` for trousers, etc.
 */
const IMAGE_CATEGORY_SUFFIXES: Record<string, string> = {
  backpacks: '_1',
  bags: '_1',
  belts: '_1',
  coats: '_25',
  cufflinks: '_1',
  gloves: '_1',
  hats: '_1',
  jackets: '_25',
  knitwear: '_25',
  pocketsquares: '_1',
  scarfs: '_1',
  shirts: '_25',
  shoes: '_1',
  shorts: '_28',
  socks: '_1',
  suits: '_29',
  suspenders: '_1',
  ties: '_1',
  trousers: '_28',
  waistcoats: '_25',
}

/**
 * Extract the product category from a Cloudinary image path.
 *
 * `products/Trousers/default/Winter/B6935_1` -> `trousers`. Normalised to
 * lower case so the suffix lookup does not care how the producer cased the
 * category. Only this segment is normalised — the rest of the path is left
 * exactly as sent.
 */
export function extractCategoryFromPath(
  imagePath: string | null | undefined,
): string | null {
  const match = imagePath?.match(/^\/*products\/([^/]+)\//i)
  return match?.[1]?.toLowerCase() ?? null
}

/** The canonical view suffix for the category a path belongs to, if known. */
export function getCategoryImageSuffix(
  imagePath: string | null | undefined,
): string | undefined {
  const category = extractCategoryFromPath(imagePath)
  return category ? IMAGE_CATEGORY_SUFFIXES[category] : undefined
}

/**
 * Rewrite a product image path to its canonical per-category render.
 *
 * `products/Coats/default/Winter/BW323_1` -> `products/Coats/default/BW323_25`
 *
 * Two corrections, both needed because the feed points at whichever asset the
 * style happened to be shot for: any folder below `products/<category>/<set>`
 * is collapsed away (the canonical render sits directly in that set, so
 * keeping a seasonal folder while swapping the suffix 404s), and the view
 * suffix is swapped for the category's. Paths outside a known category
 * — campaign art, an absolute url — are returned untouched, so this is safe
 * to apply to any image the producer sends.
 *
 * The set segment is matched as `[^/]+` rather than the literal `default`, so
 * the producer's casing survives and a set other than `default` still
 * collapses — same shape as `getOptimizedImageUrl` in `@susu/returns`.
 */
export function resolveCategoryImagePath(imagePath: string): string
export function resolveCategoryImagePath(
  imagePath: string | null | undefined,
): string | null | undefined
export function resolveCategoryImagePath(
  imagePath: string | null | undefined,
): string | null | undefined {
  const suffix = getCategoryImageSuffix(imagePath)
  if (!imagePath || !suffix) return imagePath

  const collapsed = imagePath.replace(
    /^(\/*products\/[^/]+\/[^/]+)\/(?:[^/]+\/)+([^/]+)$/i,
    '$1/$2',
  )

  return /_\d+(\.[a-z0-9]+)?$/i.test(collapsed)
    ? collapsed.replace(/_\d+(\.[a-z0-9]+)?$/i, `${suffix}$1`)
    : collapsed.replace(/([^/]+?)(\.[a-z0-9]+)?$/i, `$1${suffix}$2`)
}
